import axios from 'axios';
import { auth } from '../config/firebase';

// Yayında panel nginx üzerinden servis edildiği için API aynı origin'den
// (/api) geçer; böylece HTTPS'e taşındığında karışık içerik sorunu olmaz.
// Yerel geliştirmede doğrudan sunucuya bağlanılır.
const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const API_BASE_URL =
  process.env.REACT_APP_API_URL || (isLocalDev ? 'http://127.0.0.1:3000/api' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Firebase kimlik jetonu yaklaşık bir saat geçerlidir. Her istekte zorla
// yenilemek (getIdToken(true)) kota sınırını doldurup tüm paneli 401'e düşürür.
let cachedToken = null;
let cachedAt = 0;
let inflight = null;
const TOKEN_TTL_MS = 45 * 60 * 1000;

function rememberToken(token) {
  if (!token) return null;
  cachedToken = token;
  cachedAt = Date.now();
  return token;
}

function readCachedToken() {
  if (cachedToken && Date.now() - cachedAt < TOKEN_TTL_MS) return cachedToken;
  return cachedToken || null;
}

async function getPanelToken(force = false) {
  const user = auth.currentUser;
  if (!user) return null;
  if (!force) {
    const hit = readCachedToken();
    if (hit && Date.now() - cachedAt < TOKEN_TTL_MS) return hit;
  }
  if (inflight) return inflight;

  const run = user.getIdToken(false).then((token) => rememberToken(token)).catch((err) => {
    const fallback = readCachedToken();
    if (fallback) return fallback;
    throw err;
  });

  inflight = run.finally(() => {
    inflight = null;
  });
  return inflight;
}

// Request interceptor for adding the Firebase token
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await getPanelToken(false);
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error("Error getting Firebase token:", error);
        return Promise.reject(error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling 401s globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (error.response && error.response.status === 401 && config && !config.__retried) {
      config.__retried = true;
      try {
        cachedAt = 0;
        const token = await getPanelToken(true);
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          return api(config);
        }
      } catch (refreshErr) {
        console.error("Unauthorized request, possibly expired token");
      }
    }
    return Promise.reject(error);
  }
);

export default api;
